/**
 * API client for the public share endpoints. The server returns a
 * voter JWT on the first /share/:token call; we cache it in
 * localStorage along with the manifest so reloads are instant and
 * picks attribute to the same voter.
 */

import type {
  Bucket,
  CommentRegion,
  DownloadMode,
  DownloadAvailability,
  LinkManifest,
  SharePolicyClientDto,
} from '../types'
import { photographerDisplayName } from '../utils/photographerName'
import { adaptLegacyManifestPolicy, readClientPolicyDto } from '../state/capabilities'
import { resolvePicksApiBase } from './base'
import { responseRequestId, withRequestId } from './instrumentation'

interface ImportMetaWithEnv extends ImportMeta {
  env?: { VITE_API_BASE?: string }
}

const API_BASE = resolvePicksApiBase(
  (import.meta as ImportMetaWithEnv).env?.VITE_API_BASE as string | undefined
)

interface ShareApiResponse {
  linkId: string
  shareToken: string
  voterToken: string
  token: string
  photographer: {
    name: string
    email?: string | null
    brandLogoUrl?: string | null
    brandColor?: string | null
    instagramUrl?: string | null
    facebookUrl?: string | null
  }
  event: { title: string; dateLabel: string | null }
  quotaTarget: { min: number; max: number }
  expiresAt: number
  policy?: unknown
  picksEnabled?: boolean
  downloadEnabled?: DownloadMode
  zipUrl?: string | null
  photos: Array<{
    id: string
    position: number
    isRecommended: boolean
    category: string
    people?: string[]
    aspectW?: number | null
    aspectH?: number | null
    thumbUrl: string
    displayUrl: string
    zoomUrl: string
    downloadUrl?: string | null
    downloadAvailability?: DownloadAvailability
  }>
  comments?: Array<{
    id: string
    photoId: string | null
    body: string
    parentCommentId?: string | null
    mentions?: string[]
    createdAt: number
    region?: CommentRegion | null
  }>
}

const VOTER_KEY_PREFIX = 'shotselect-voter-jwt-'
const INVITE_KEY_PREFIX = 'shotselect-invite-'
const ACCESS_KEY_PREFIX = 'shotselect-access-'

type ClientAbility = keyof SharePolicyClientDto['can']

const clientPolicies = new Map<string, SharePolicyClientDto>()
let refreshCapabilities: ((shareToken: string) => void | Promise<void>) | null = null

/**
 * Register the manifest store's refresh path without introducing an API/store
 * import cycle. The call begins synchronously, so the old manifest disappears
 * from the actionable route before the rejected mutation returns to the user.
 */
export function setCapabilityRefreshHandler(
  handler: ((shareToken: string) => void | Promise<void>) | null
): void {
  refreshCapabilities = handler
}

/** Record the latest server-projected grant for one isolated share link. */
export function setClientPolicySnapshot(
  shareToken: string,
  policy: SharePolicyClientDto | null
): void {
  if (policy) clientPolicies.set(shareToken, policy)
  else clientPolicies.delete(shareToken)
}

function requireAbility(shareToken: string, ability: ClientAbility): SharePolicyClientDto {
  const policy = clientPolicies.get(shareToken)
  if (!policy) throw new ApiError(401, 'session_required')
  if (!policy.can[ability]) throw new ApiError(403, 'capability_denied')
  return policy
}

const REFRESH_CAPABILITY_CODES = new Set([
  'session_required',
  'policy_changed',
  'capability_denied',
  'tier_not_granted',
  'unsupported_policy_version',
  'malformed_policy',
])

function handleCapabilityFailure(shareToken: string, error: ApiError): void {
  if (!REFRESH_CAPABILITY_CODES.has(error.code)) return
  // Deny immediately. The old snapshot must not authorize another click while
  // the manifest/JWT refresh is in flight or if that refresh fails.
  setClientPolicySnapshot(shareToken, null)
  if (error.code === 'session_required' || error.code === 'policy_changed') {
    removeStored(`${VOTER_KEY_PREFIX}${shareToken}`)
  }
  void refreshCapabilities?.(shareToken)
}

async function apiErrorFromResponse(
  res: Response,
  requestId: string,
  shareToken: string,
  body?: unknown
): Promise<ApiError> {
  let parsed = body
  if (parsed === undefined) {
    try {
      parsed = await res.json()
    } catch {
      parsed = null
    }
  }
  const code = (parsed as { error?: string } | null)?.error ?? `http_${res.status}`
  const error = new ApiError(res.status, code, responseRequestId(res, requestId))
  handleCapabilityFailure(shareToken, error)
  return error
}

/**
 * Ask the studio to send a code to this address.
 *
 * The answer is the same whether the address was invited or not — the worker
 * will not say, because a truthful "no" would turn a share URL into a way to
 * enumerate a couple's guest list.
 */
export async function requestGalleryAccess(
  shareToken: string,
  email: string
): Promise<{ ok: boolean; devCode?: string }> {
  const res = await fetch(`${API_BASE}/share/${encodeURIComponent(shareToken)}/access/request`, {
    method: 'POST',
    headers: withRequestId({ 'Content-Type': 'application/json' }).headers,
    body: JSON.stringify({ email: email.trim().toLowerCase() }),
  })
  const body = (await res.json().catch(() => null)) as { devCode?: string; error?: string } | null
  if (!res.ok) throw new ApiError(res.status, body?.error ?? `http_${res.status}`)
  return { ok: true, devCode: body?.devCode }
}

/** Exchange the code for a pass, and remember it so this is once per device. */
export async function verifyGalleryAccess(
  shareToken: string,
  email: string,
  code: string
): Promise<void> {
  const res = await fetch(`${API_BASE}/share/${encodeURIComponent(shareToken)}/access/verify`, {
    method: 'POST',
    headers: withRequestId({ 'Content-Type': 'application/json' }).headers,
    body: JSON.stringify({ email: email.trim().toLowerCase(), code: code.trim() }),
  })
  const body = (await res.json().catch(() => null)) as {
    accessPass?: string
    error?: string
  } | null
  if (!res.ok || !body?.accessPass) {
    throw new ApiError(res.status, body?.error ?? `http_${res.status}`)
  }
  writeStored(`${ACCESS_KEY_PREFIX}${shareToken}`, body.accessPass)
}

/** The pass this browser holds for a restricted gallery, if any. */
export function readAccessPass(shareToken: string): string | null {
  return readStored(`${ACCESS_KEY_PREFIX}${shareToken}`)
}

/**
 * The invitation this browser arrived through.
 *
 * Read from the URL on the first visit and remembered, because the client
 * bookmarks the gallery or reopens it from history — and a return visit with
 * the `&i=` stripped would otherwise look like a brand-new stranger.
 */
export function readInviteToken(shareToken: string): string | null {
  if (typeof window !== 'undefined') {
    const hash = window.location.hash
    const qs = hash.includes('?') ? hash.slice(hash.indexOf('?') + 1) : ''
    const fromUrl = new URLSearchParams(qs).get('i')
    if (fromUrl) {
      writeStored(`${INVITE_KEY_PREFIX}${shareToken}`, fromUrl)
      return fromUrl
    }
  }
  return readStored(`${INVITE_KEY_PREFIX}${shareToken}`)
}
const VOTER_TOKEN_PREFIX = 'shotselect-voter-token-'
const MAX_VOTER_NAME_LENGTH = 80
// Per-link gallery passcode (the studio's download_pin doubles as the gallery
// passcode). Persisted so a passcode-gated gallery survives a reload without
// re-prompting, and so every download/zip URL can be authorized with `?pin=`.
const PIN_PREFIX = 'shotselect-pin-'

function readStored(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function writeStored(key: string, value: string): void {
  try {
    localStorage.setItem(key, value)
  } catch {
    /* ignore */
  }
}

function removeStored(key: string): void {
  try {
    localStorage.removeItem(key)
  } catch {
    /* ignore */
  }
}

/** The passcode the viewer entered for this gallery, if any. */
export function getStoredPin(shareToken: string): string | null {
  const v = readStored(`${PIN_PREFIX}${shareToken}`)
  return v && v.length > 0 ? v : null
}

/** Persist (or clear) the gallery passcode for this link. */
export function setStoredPin(shareToken: string, pin: string | null): void {
  if (pin && pin.length > 0) writeStored(`${PIN_PREFIX}${shareToken}`, pin)
  else removeStored(`${PIN_PREFIX}${shareToken}`)
}

/**
 * Append the link's gallery passcode to a download/zip URL as `?pin=`. The
 * worker requires it on every download endpoint when the link has a pin; without
 * it the URL 401s silently and the download looks like a dead link. No stored
 * pin → the URL is returned unchanged (un-pinned link).
 */
export function withPin(url: string, shareToken: string): string {
  const pin = getStoredPin(shareToken)
  if (!pin || /(?:\?|&)pin=/.test(url)) return url
  const sep = url.includes('?') ? '&' : '?'
  return `${url}${sep}pin=${encodeURIComponent(pin)}`
}

/**
 * Attach the two browser-held access proofs required by independently gated
 * media routes. They remain URL credentials only and never enter the policy
 * DTO, analytics metadata, or application state.
 */
export function withAccessCredentials(url: string, shareToken: string): string {
  let next = withPin(url, shareToken)
  const accessPass = readAccessPass(shareToken)
  if (!accessPass || /(?:\?|&)ap=/.test(next)) return next
  next += `${next.includes('?') ? '&' : '?'}ap=${encodeURIComponent(accessPass)}`
  return next
}

/** The sneak-peek mini-gallery payload — view-only, no voter identity. */
export interface SneakPeekManifest {
  photographer: { name: string; brandLogoUrl?: string | null; brandColor?: string | null }
  event: { title: string; dateLabel: string | null }
  photos: Array<{ id: string; position: number; displayUrl: string }>
}

/**
 * Fetch the view-only sneak-peek mini-gallery. No voter token, no pin — the
 * slug IS the capability, independently revocable from the main share.
 */
export async function fetchSneakPeek(sneakToken: string): Promise<SneakPeekManifest> {
  const res = await fetch(`${API_BASE}/share/sneak/${encodeURIComponent(sneakToken)}`, {
    method: 'GET',
    cache: 'no-store',
  })
  if (!res.ok) {
    let body: unknown = null
    try {
      body = await res.json()
    } catch {
      /* ignore */
    }
    const code = (body as { error?: string } | null)?.error ?? `http_${res.status}`
    throw new ApiError(res.status, code, undefined, parseExpiredContact(body))
  }
  return (await res.json()) as SneakPeekManifest
}

export async function fetchManifest(
  shareToken: string,
  /**
   * The gallery passcode. When omitted, any previously-stored pin for this link
   * is used (so a reload doesn't re-prompt). A passcode-gated link with no pin
   * throws ApiError(401, 'passcode_required') — the caller shows the prompt.
   */
  pin?: string
): Promise<{
  manifest: LinkManifest
  jwt: string
  voterToken: string
}> {
  const existingVoter = readStored(`${VOTER_TOKEN_PREFIX}${shareToken}`) ?? ''
  const effectivePin = pin ?? getStoredPin(shareToken) ?? ''
  const params = new URLSearchParams()
  if (existingVoter) params.set('vt', existingVoter)
  if (effectivePin) params.set('pin', effectivePin)
  // Which invitation brought this visitor. The studio mints one token per
  // address it sends to, so forwarding this along is what lets the photographer
  // see WHO opened the gallery instead of an anonymous "Reviewer 2" — before
  // the visitor has typed anything. It is a label, never access: the share
  // token alone still opens the gallery.
  const invite = readInviteToken(shareToken)
  if (invite) params.set('i', invite)
  // Proof this browser holds an invited address. Only a restricted link asks
  // for it; carrying it always keeps the fetch identical either way.
  const accessPass = readAccessPass(shareToken)
  if (accessPass) params.set('ap', accessPass)
  const qs = params.toString()
  const url = `${API_BASE}/share/${encodeURIComponent(shareToken)}${qs ? `?${qs}` : ''}`
  const { headers, requestId } = withRequestId()
  // no-store: the manifest is stateful (uploading → active, paused ↔ resumed).
  // Browsers heuristically cache un-headered 410/423 answers, which made a
  // resumed link keep replaying the cached "paused" error on every retry —
  // the gallery looked permanently down until the viewer cleared site data.
  const res = await fetch(url, { method: 'GET', headers, cache: 'no-store' })
  if (!res.ok) {
    let body: unknown = null
    try {
      body = await res.json()
    } catch {
      /* ignore */
    }
    const code = (body as { error?: string } | null)?.error ?? `http_${res.status}`
    // A wrong/stale pin must not stick around and silently re-fail the retry.
    if (code === 'passcode_required' || code === 'passcode_incorrect') {
      setStoredPin(shareToken, null)
    }
    // A 410 (expired/revoked/paused) carries the photographer's contact so the
    // expired screen can tell the client who to reach out to.
    throw new ApiError(
      res.status,
      code,
      responseRequestId(res, requestId),
      parseExpiredContact(body)
    )
  }
  const data = (await res.json()) as ShareApiResponse

  const policy =
    data.policy === undefined
      ? adaptLegacyManifestPolicy({
          picksEnabled: data.picksEnabled,
          downloadEnabled: data.downloadEnabled,
          expiresAt: data.expiresAt,
        })
      : readClientPolicyDto(data.policy)
  if (!policy) {
    const rawVersion = (data.policy as { policyVersion?: unknown } | null)?.policyVersion
    throw new ApiError(
      409,
      typeof rawVersion === 'number' && rawVersion !== 0 && rawVersion !== 1
        ? 'unsupported_policy_version'
        : 'malformed_policy',
      responseRequestId(res, requestId)
    )
  }
  if (!policy.can.view) {
    throw new ApiError(403, 'capability_denied', responseRequestId(res, requestId))
  }

  writeStored(`${VOTER_KEY_PREFIX}${shareToken}`, data.token)
  writeStored(`${VOTER_TOKEN_PREFIX}${shareToken}`, data.voterToken)
  setClientPolicySnapshot(shareToken, policy)
  // The pin worked → remember it so downloads/zip + reloads stay authorized.
  if (effectivePin) setStoredPin(shareToken, effectivePin)

  const manifest: LinkManifest = {
    linkId: data.linkId,
    shareToken: data.shareToken,
    photographer: {
      name: photographerDisplayName(data.photographer.name),
      handle: '',
      email: data.photographer.email ?? '',
      accentColor: data.photographer.brandColor ?? undefined,
      brandLogoUrl: data.photographer.brandLogoUrl ?? null,
      instagramUrl: data.photographer.instagramUrl ?? null,
      facebookUrl: data.photographer.facebookUrl ?? null,
    },

    event: {
      title: data.event.title,
      dateLabel: data.event.dateLabel ?? '',
    },
    quotaTarget: data.quotaTarget,
    expiresAt: new Date(data.expiresAt).toISOString(),
    policy,
    // Compatibility projection only. New code reads `policy.can` and
    // `policy.downloads`; downloads no longer have a legacy twin.
    picksEnabled: policy.can.select,
    // Zip-all endpoint; null when downloads are off. The SSR twin already shows
    // a "Download all" button from this; the SPA mirrors it in the gallery.
    zipUrl: data.zipUrl ? withAccessCredentials(data.zipUrl, shareToken) : null,
    photos: data.photos
      .sort((a, b) => a.position - b.position)
      .map((p) => ({
        id: p.id,
        category: p.category,
        people: p.people ?? [],
        aspectW: p.aspectW ?? undefined,
        aspectH: p.aspectH ?? undefined,
        isRecommended: p.isRecommended,
        thumbUrl: withAccessCredentials(p.thumbUrl, shareToken),
        displayUrl: withAccessCredentials(p.displayUrl, shareToken),
        zoomUrl: withAccessCredentials(p.zoomUrl, shareToken),
        downloadUrl: p.downloadUrl ? withAccessCredentials(p.downloadUrl, shareToken) : null,
        downloadAvailability: p.downloadAvailability,
      })),
    comments: (data.comments ?? []).map((cm) => ({
      id: cm.id,
      photoId: cm.photoId,
      body: cm.body,
      parentCommentId: cm.parentCommentId ?? null,
      mentions: cm.mentions ?? [],
      createdAt: cm.createdAt,
      region: cm.region ?? null,
    })),
  }

  return { manifest, jwt: data.token, voterToken: data.voterToken }
}

/**
 * Tell the studio what to call you.
 *
 * Separate from submitPicks because the name used to ride along ONLY with a
 * finalized submission: someone who typed their name and then closed the tab —
 * or who was only ever going to browse — stayed anonymous, and the photographer
 * saw "Reviewer 2". The name is worth recording the moment it is given.
 *
 * Best-effort by design: this is a courtesy, never a gate on reviewing. A
 * failure is swallowed so a flaky network cannot block the actual work, and
 * finalize still carries the name as a second chance.
 */
export async function setVoterDisplayName(
  shareToken: string,
  name: string,
  email?: string
): Promise<boolean> {
  const displayName = name.trim().slice(0, MAX_VOTER_NAME_LENGTH)
  const address = email?.trim().toLowerCase() ?? ''
  if (!displayName && !address) return false
  try {
    requireAbility(shareToken, 'view')
    const jwt = readStored(`${VOTER_KEY_PREFIX}${shareToken}`)
    // No voter JWT yet (the manifest mints it) — finalize will carry the name.
    if (!jwt) return false
    const { headers, requestId } = withRequestId({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${jwt}`,
    })
    const res = await fetch(`${API_BASE}/share/${encodeURIComponent(shareToken)}/voter`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({
        ...(displayName ? { displayName } : {}),
        ...(address ? { email: address } : {}),
      }),
    })
    if (!res.ok) {
      const error = await apiErrorFromResponse(res, requestId, shareToken)
      if (REFRESH_CAPABILITY_CODES.has(error.code)) return false
    }
    return res.ok
  } catch {
    return false
  }
}

/**
 * A pick as it goes over the wire. `clear` is a withdrawal — the worker deletes
 * the row — and is deliberately NOT part of `Bucket`, which models the three
 * verdicts a client can actually choose.
 */
export type WirePick = {
  photoId: string
  bucket: Bucket | 'clear'
  note?: string
  /** Local bookkeeping on a stored Pick; never sent (the body is mapped below). */
  updatedAt?: number
}

export async function submitPicks(
  shareToken: string,
  picks: WirePick[],
  finalized = false,
  opts: { voterName?: string } = {}
): Promise<{ accepted: number; finalized: boolean }> {
  requireAbility(shareToken, 'select')
  // Keep the optional reviewer identity compatible across Worker generations:
  // older deployments persist it through PUT /voter, while current Workers also
  // accept it with the final picks for atomic identity + selection storage.
  const voterName = opts.voterName?.trim().slice(0, MAX_VOTER_NAME_LENGTH) || undefined
  const jwt = readStored(`${VOTER_KEY_PREFIX}${shareToken}`)
  if (!jwt) throw new ApiError(401, 'no_voter_jwt')

  if (finalized && voterName) {
    const { headers, requestId } = withRequestId({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${jwt}`,
    })
    const res = await fetch(`${API_BASE}/share/${encodeURIComponent(shareToken)}/voter`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ displayName: voterName }),
    })
    if (!res.ok) {
      let body: unknown = null
      try {
        body = await res.json()
      } catch {
        /* ignore */
      }
      throw await apiErrorFromResponse(res, requestId, shareToken, body)
    }
  }

  const { headers, requestId } = withRequestId({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${jwt}`,
  })
  const res = await fetch(`${API_BASE}/share/${encodeURIComponent(shareToken)}/picks`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      finalized,
      ...(voterName ? { voterName } : {}),
      picks: picks.map((p) => ({
        photoId: p.photoId,
        bucket: p.bucket,
        note: p.note,
      })),
    }),
  })
  if (!res.ok) {
    let body: unknown = null
    try {
      body = await res.json()
    } catch {
      /* ignore */
    }
    throw await apiErrorFromResponse(res, requestId, shareToken, body)
  }
  const data = (await res.json()) as { accepted: number; finalized: boolean }
  return data
}

/**
 * Leave a comment on a photo (or the gallery in general when photoId is null).
 * Mirrors submitPicks: the voter JWT authenticates; the worker derives identity
 * server-side. An optional `region` anchors the comment to a drawn rectangle/
 * point on the photo (normalized [0,1] markup). Returns the new comment id +
 * createdAt + the (server-clamped) region echoed back.
 */
export async function submitComment(
  shareToken: string,
  photoId: string | null,
  body: string,
  parentCommentId: string | null = null,
  region?: CommentRegion | null
): Promise<{ id: string; createdAt: number; region: CommentRegion | null }> {
  requireAbility(shareToken, 'comment')
  const jwt = readStored(`${VOTER_KEY_PREFIX}${shareToken}`)
  if (!jwt) throw new ApiError(401, 'no_voter_jwt')
  const { headers, requestId } = withRequestId({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${jwt}`,
  })
  const res = await fetch(`${API_BASE}/share/${encodeURIComponent(shareToken)}/comments`, {
    method: 'POST',
    headers,
    body: JSON.stringify(
      region ? { photoId, body, parentCommentId, region } : { photoId, body, parentCommentId }
    ),
  })
  if (!res.ok) {
    let errBody: unknown = null
    try {
      errBody = await res.json()
    } catch {
      /* ignore */
    }
    throw await apiErrorFromResponse(res, requestId, shareToken, errBody)
  }
  const data = (await res.json()) as {
    id: string
    createdAt: number
    region?: CommentRegion | null
  }
  return { id: data.id, createdAt: data.createdAt, region: data.region ?? null }
}

/** A public comment row as shipped in the manifest. */
export type ManifestComment = NonNullable<LinkManifest['comments']>[number]

/**
 * Re-fetch the link's public comments. The manifest only ships the comments that
 * existed at first load, so a studio reply that lands after the viewer opened the
 * gallery is otherwise invisible. The photo viewer calls this when it opens (or
 * regains focus) and merges the result with its local optimistic echoes.
 *
 * This re-hits the manifest endpoint (the comments live there) rather than adding
 * a parallel route — single source of truth. Returns [] on any failure so a comment
 * refresh can never break the viewer.
 */
export async function fetchComments(shareToken: string): Promise<ManifestComment[]> {
  try {
    const { manifest } = await fetchManifest(shareToken)
    return manifest.comments ?? []
  } catch {
    return []
  }
}

/**
 * Delivery-link sign-off. A download-only gallery (picksEnabled = false) has no
 * picks flow; this is the couple's terminal action. Posts to the worker
 * `/approve` endpoint (voter JWT auth) which records a client_link_event the
 * studio can see. `decision` is 'approve' or 'request_changes'.
 */
export async function approveDelivery(
  shareToken: string,
  decision: 'approve' | 'request_changes',
  note?: string
): Promise<{ decision: string }> {
  requireAbility(shareToken, 'approveDelivery')
  const jwt = readStored(`${VOTER_KEY_PREFIX}${shareToken}`)
  if (!jwt) throw new ApiError(401, 'no_voter_jwt')
  const { headers, requestId } = withRequestId({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${jwt}`,
  })
  const res = await fetch(`${API_BASE}/share/${encodeURIComponent(shareToken)}/approve`, {
    method: 'POST',
    headers,
    body: JSON.stringify(note ? { decision, note } : { decision }),
  })
  if (!res.ok) {
    let errBody: unknown = null
    try {
      errBody = await res.json()
    } catch {
      /* ignore */
    }
    throw await apiErrorFromResponse(res, requestId, shareToken, errBody)
  }
  const data = (await res.json()) as { decision: string }
  return { decision: data.decision }
}

export type ShareEventType =
  'gallery_viewed' | 'review_opened' | 'photo_opened' | 'photo_downloaded' | 'share_sheet_opened'

export async function trackShareEvent(
  shareToken: string,
  type: ShareEventType,
  options: {
    visitId?: string
    photoId?: string | null
    metadata?: Record<string, string | number | boolean | null>
  } = {}
): Promise<void> {
  try {
    requireAbility(shareToken, 'view')
  } catch {
    return
  }
  const jwt = readStored(`${VOTER_KEY_PREFIX}${shareToken}`)
  if (!jwt) return
  const { headers } = withRequestId({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${jwt}`,
  })
  const res = await fetch(`${API_BASE}/share/${encodeURIComponent(shareToken)}/events`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      type,
      ...(options.visitId ? { visitId: options.visitId } : {}),
      photoId: options.photoId ?? null,
      metadata: options.metadata ?? null,
    }),
  })
  if (!res.ok) {
    // Engagement events must never break gallery/review/download workflows.
    await apiErrorFromResponse(res, '', shareToken)
    return
  }
}

/**
 * The photographer's name + a client-facing contact handle. The worker now
 * includes this on a 410 (expired/revoked/paused) manifest response so a
 * returning client whose link lapsed still sees who to reach out to — the active
 * manifest already carries it, but a 410 used to be a contact-less dead end.
 */
export interface ExpiredContact {
  name: string
  email: string | null
  phone: string | null
  website: string | null
  brandColor: string | null
}

function parseExpiredContact(body: unknown): ExpiredContact | null {
  const p = (body as { photographer?: unknown } | null)?.photographer
  if (!p || typeof p !== 'object') return null
  const r = p as Record<string, unknown>
  const str = (v: unknown): string | null => (typeof v === 'string' && v.length > 0 ? v : null)
  return {
    name: photographerDisplayName(str(r.name)),
    email: str(r.email),
    phone: str(r.phone),
    website: str(r.website),
    brandColor: str(r.brandColor),
  }
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    public requestId?: string,
    /**
     * Present on a 410 expired/revoked manifest response: the photographer's
     * name + contact handle so the expired screen isn't a dead end.
     */
    public photographer?: ExpiredContact | null
  ) {
    super(`api_error_${status}_${code}`)
  }
}

/**
 * A returning client whose link has expired/been revoked asks the studio to send
 * a fresh one. Posts to the worker's `/refresh-request` (no voter JWT — a dead
 * link never minted one); the worker records a `refresh_requested` event the
 * studio sees in its link tracking. Best-effort: resolves on success, throws an
 * ApiError on a hard failure so the UI can show a retry.
 */
export async function requestRefresh(shareToken: string): Promise<void> {
  const existingVoter = readStored(`${VOTER_TOKEN_PREFIX}${shareToken}`) ?? ''
  const qs = existingVoter ? `?vt=${encodeURIComponent(existingVoter)}` : ''
  const { headers, requestId } = withRequestId()
  const res = await fetch(
    `${API_BASE}/share/${encodeURIComponent(shareToken)}/refresh-request${qs}`,
    { method: 'POST', headers }
  )
  if (!res.ok) {
    let body: unknown = null
    try {
      body = await res.json()
    } catch {
      /* ignore */
    }
    const code = (body as { error?: string } | null)?.error ?? `http_${res.status}`
    throw new ApiError(res.status, code, responseRequestId(res, requestId))
  }
}

/**
 * A reply to this reviewer's comment, or a comment that @mentioned them.
 * The worker writes one row per recipient and de-dupes a reply that also
 * mentions the parent author, so a single comment never notifies twice.
 */
export interface ReviewNotification {
  id: string
  type: 'mention' | 'reply'
  commentId: string | null
  photoId: string | null
  createdAt: number
}

/**
 * The reviewer's unread notifications for this link.
 *
 * Read-only and best-effort: a reviewer who cannot reach this endpoint should
 * still be able to review, so every failure resolves to an empty list rather
 * than surfacing an error. Anonymous callers have nothing to fetch — the feed
 * is keyed on the voter token, which only exists once a manifest was served.
 */
export async function fetchNotifications(shareToken: string): Promise<ReviewNotification[]> {
  try {
    requireAbility(shareToken, 'view')
  } catch {
    return []
  }
  const jwt = readStored(`${VOTER_KEY_PREFIX}${shareToken}`)
  if (!jwt) return []
  try {
    const res = await fetch(`${API_BASE}/share/${encodeURIComponent(shareToken)}/notifications`, {
      headers: { Authorization: `Bearer ${jwt}` },
    })
    if (!res.ok) {
      await apiErrorFromResponse(res, '', shareToken)
      return []
    }
    const data = (await res.json()) as { notifications?: ReviewNotification[] }
    return data.notifications ?? []
  } catch {
    return []
  }
}

/** Clear the caller's unread notifications. Best-effort, same as the read. */
export async function markNotificationsRead(shareToken: string): Promise<void> {
  try {
    requireAbility(shareToken, 'view')
  } catch {
    return
  }
  const jwt = readStored(`${VOTER_KEY_PREFIX}${shareToken}`)
  if (!jwt) return
  try {
    const res = await fetch(
      `${API_BASE}/share/${encodeURIComponent(shareToken)}/notifications/read`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
        body: '{}',
      }
    )
    if (!res.ok) await apiErrorFromResponse(res, '', shareToken)
  } catch {
    /* best-effort */
  }
}
